import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RotateCw, Pencil, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

export const failureText = (m) =>
  [m.error_code ? `Meta error ${m.error_code}${m.error_subcode ? `/${m.error_subcode}` : ""}` : m.code ? `HTTP ${m.code}` : null,
   m.error_message, m.error_details && m.error_details !== m.error_message ? m.error_details : null, m.error]
    .filter(Boolean).join(" · ") || "Unknown error";

const bodyText = (p) => p?.text?.body || p?.interactive?.body?.text || "";

function RetryControls({ r, onDone }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(bodyText(r.meta?.payload));
  const [busy, setBusy] = useState(false);
  const canEdit = r.meta?.payload && r.meta.payload.type !== "template";
  const retry = async (edited) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/whatsapp/failures/${r.message_id}/retry`, edited ? { text } : {});
      toast.success(data.mode === "template" ? "Sent again using the approved template" : "Sent again");
      setEditing(false);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
      onDone();
    } finally {
      setBusy(false);
    }
  };
  if (r.retry_status === "sent") {
    return (
      <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1" data-testid={`wa-retry-done-${r.message_id}`}>
        <CheckCircle2 className="w-3.5 h-3.5" /> Sent again {new Date(r.retried_at).toLocaleString("en-IN")}{r.retried_by ? ` by ${r.retried_by}` : ""}
      </div>
    );
  }
  if (!r.meta?.payload) return <div className="mt-2 text-xs text-slate-500">Recorded before resending was possible.</div>;
  return (
    <div className="mt-3 space-y-2">
      {r.retry_status === "failed" && (
        <div className="text-xs text-rose-700" data-testid={`wa-retry-failed-${r.message_id}`}>Last retry failed: {failureText(r.retry_error || {})}</div>
      )}
      {editing && (
        <>
          <Textarea value={text} onChange={e => setText(e.target.value)} rows={3} className="rounded-xl text-sm" data-testid={`wa-retry-text-${r.message_id}`} />
          {r.meta.payload.type === "interactive" && <div className="text-xs text-slate-500">Edited messages go as plain text, without buttons.</div>}
        </>
      )}
      <div className="flex gap-2">
        {!editing ? (
          <>
            <Button size="sm" disabled={busy} onClick={() => retry(false)} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid={`wa-retry-${r.message_id}`}>
              <RotateCw className="w-3.5 h-3.5 mr-1" /> Send again
            </Button>
            {canEdit && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(true)} className="rounded-full" data-testid={`wa-edit-${r.message_id}`}>
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit & send
              </Button>
            )}
          </>
        ) : (
          <>
            <Button size="sm" disabled={busy || !text.trim()} onClick={() => retry(true)} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid={`wa-edit-send-${r.message_id}`}>
              Send edited text
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="rounded-full" data-testid={`wa-edit-cancel-${r.message_id}`}>Cancel</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function WhatsAppFailures({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/whatsapp/failures").then(r => setRows(r.data))
    .catch(err => toast.error(err.response?.data?.detail || "Failed to load"));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (rows === null) return null;
  if (!rows.length) {
    return <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="wa-failures-empty">No failed WhatsApp sends.</div>;
  }
  return (
    <div className="space-y-3" data-testid="wa-failures-list">
      {rows.map(r => (
        <Card key={r.message_id} className="p-4 rounded-2xl border-rose-200 bg-white" data-testid={`wa-failure-${r.message_id}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <span className="font-semibold">{r.user?.name || "Unknown contact"}</span>
            <span className="text-slate-500">+{r.to}</span>
            <span className="text-xs text-slate-500">· {r.kind}{r.meta?.template ? ` (${r.meta.template})` : ""} · {new Date(r.timestamp).toLocaleString("en-IN")}</span>
          </div>
          <div className="text-sm text-rose-700 mt-1 break-words" data-testid={`wa-failure-reason-${r.message_id}`}>{failureText(r.meta || {})}</div>
          {bodyText(r.meta?.payload) && <div className="text-xs text-slate-600 mt-1 line-clamp-2">“{bodyText(r.meta.payload)}”</div>}
          <RetryControls r={r} onDone={load} />
        </Card>
      ))}
    </div>
  );
}
